import { useTranslation } from "react-i18next";
import { z } from "zod";
import { DateAdapter } from "@myphx-engineering/date-utils/client";

//#region src/hooks/form-validation/use-split-dob-zod-schema.ts
const useSplitDobZodSchema = (withMinimumAge = true) => {
	const { t } = useTranslation("pds");
	const fields = {
		dobMonth: z.string().min(1, { message: t("common.formErrors.dob.month.required") }).refine((v) => {
			if (!/^\d+$/.test(v)) return false;
			const n = Number(v);
			return n >= 1 && n <= 12;
		}, { message: t("common.formErrors.dob.month.invalid") }),
		dobDay: z.string().min(1, { message: t("common.formErrors.dob.day.required") }).refine((v) => {
			if (!/^\d+$/.test(v)) return false;
			const n = Number(v);
			return n >= 1 && n <= 31;
		}, { message: t("common.formErrors.dob.day.invalid") }),
		dobYear: z.string().min(1, { message: t("common.formErrors.dob.year.required") }).refine((v) => {
			if (!/^\d+$/.test(v)) return false;
			const n = Number(v);
			return v.length === 4 && n >= 1900 && n <= (/* @__PURE__ */ new Date()).getFullYear();
		}, { message: t("common.formErrors.dob.year.invalid") })
	};
	const superRefine = (data, ctx) => {
		const month = Number.parseInt(data.dobMonth, 10);
		const day = Number.parseInt(data.dobDay, 10);
		const year = Number.parseInt(data.dobYear, 10);
		if (Number.isNaN(month) || Number.isNaN(day) || Number.isNaN(year)) return;
		if (year < 1900) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: t("common.formErrors.dob.year.invalid"),
				path: ["dobYear"]
			});
			return z.NEVER;
		}
		if (!DateAdapter.isValidDate(year, month, day)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: t("common.formErrors.dob.invalid"),
				path: ["dobMonth"]
			});
			return z.NEVER;
		}
		if (DateAdapter.isFutureDate(year, month, day)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: t("common.formErrors.dob.future"),
				path: ["dobDate"]
			});
			return z.NEVER;
		}
		if (withMinimumAge && !DateAdapter.validateAge(year, month, day, 13)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: t("common.formErrors.dob.tooYoung"),
				path: ["dobDate"]
			});
			return z.NEVER;
		}
	};
	return {
		fields,
		superRefine
	};
};

//#endregion
export { useSplitDobZodSchema };